import React, { useState, useEffect, useRef } from 'react';
import axiosInstance from '../../axios/axiosInstance';

const Spinner = () => (
    <div style={{ width: 18, height: 18, border: '2px solid #1B75F0', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />
);

const Card = ({ title, children, style = {} }) => (
    <div style={{ background: '#fff', borderRadius: 12, border: '1px solid #e2e8f0', padding: '20px 22px', ...style }}>
        {title && <div style={{ fontSize: 13, fontWeight: 700, color: '#1e293b', marginBottom: 16 }}>{title}</div>}
        {children}
    </div>
);

const StatBox = ({ label, value, sub, color = '#1B75F0' }) => (
    <div style={{ background: '#f8fafc', borderRadius: 10, padding: '14px 16px', flex: 1 }}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
        <div style={{ fontSize: 26, fontWeight: 800, color, marginTop: 4 }}>{value}</div>
        {sub && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{sub}</div>}
    </div>
);

const BillingPage = () => {
    const firmId = localStorage.getItem('firmId');
    const isSuperAdmin = (localStorage.getItem('userRole') || '') === 'SuperAdmin';
    const [sub, setSub] = useState(null);
    const [usage, setUsage] = useState(null);
    const [history, setHistory] = useState([]);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [setupLoading, setSetupLoading] = useState(false);
    const [selectingPlanId, setSelectingPlanId] = useState(null);
    const [chargeRunning, setChargeRunning] = useState(false);
    const [toast, setToast] = useState(null);
    const stripeRef = useRef(null);

    const reloadHistory = async () => {
        try {
            const histRes = await axiosInstance.get(`/billing/history/${firmId}`);
            setHistory(Array.isArray(histRes.data) ? histRes.data : []);
        } catch {
            setHistory([]);
        }
    };

    const reloadSubscription = async () => {
        const subRes = await axiosInstance.get(`/billing/subscription/${firmId}`);
        setSub(subRes.data);
        return subRes.data;
    };

    const showToast = (msg, type = 'success') => {
        setToast({ msg, type });
        setTimeout(() => setToast(null), 3500);
    };

    const loadPlansCatalog = async () => {
        // Prefer /catalog (active only). Fall back to /all if catalog route not published yet.
        try {
            const res = await axiosInstance.get('/plans/catalog');
            const list = Array.isArray(res.data) ? res.data : [];
            if (list.length > 0) return list;
        } catch {
            /* try fallback */
        }
        try {
            const res = await axiosInstance.get('/plans/all');
            const list = Array.isArray(res.data) ? res.data : [];
            return list.filter(p => p.isActive !== false);
        } catch {
            return null;
        }
    };

    useEffect(() => {
        if (!firmId) return;
        const load = async () => {
            setLoading(true);
            try {
                const [subRes, usageRes, histRes] = await Promise.allSettled([
                    axiosInstance.get(`/billing/subscription/${firmId}`),
                    axiosInstance.get(`/billing/usage/${firmId}`),
                    axiosInstance.get(`/billing/history/${firmId}`),
                ]);
                if (subRes.status === 'fulfilled') setSub(subRes.value.data);
                if (usageRes.status === 'fulfilled') setUsage(usageRes.value.data);
                else setUsage(null);
                if (histRes.status === 'fulfilled') {
                    setHistory(Array.isArray(histRes.value.data) ? histRes.value.data : []);
                } else {
                    setHistory([]);
                }

                const catalog = await loadPlansCatalog();
                if (catalog === null) {
                    setPlans([]);
                    showToast('Failed to load plans catalog. Publish the latest API and retry.', 'error');
                } else {
                    setPlans(catalog);
                }

                const failed = [subRes, usageRes].filter(r => r.status === 'rejected');
                if (failed.length) showToast('Failed to load some billing info.', 'error');
            } catch { showToast('Failed to load billing info.', 'error'); }
            finally { setLoading(false); }
        };
        load();
    }, [firmId]);

    const applyPlanPricingToUsage = (planLike, prev) => {
        const pricing = planLike?.pricing || {
            companyMonthly: planLike?.companyMonthlyPrice ?? 0,
            employeeMonthly: planLike?.employeeMonthlyPrice ?? 0,
            contractorMonthly: planLike?.contractorMonthlyPrice ?? 0,
        };
        if (!prev) return { pricing };
        return { ...prev, pricing, planId: planLike?.planId ?? planLike?.id ?? prev.planId, planName: planLike?.planName ?? planLike?.name ?? prev.planName };
    };

    const handleSelectPlan = async (planId) => {
        if (!firmId || !planId) return;
        setSelectingPlanId(planId);
        const catalogPlan = plans.find(p => Number(p.id) === Number(planId));
        try {
            const body = { planId: Number(planId) };
            if (isSuperAdmin) body.firmId = Number(firmId);
            const res = await axiosInstance.post('/billing/select-plan', body);
            setSub(res.data);
            // Apply rates immediately from select response / catalog so UI never lags behind "Current"
            setUsage(prev => applyPlanPricingToUsage(res.data?.pricing ? res.data : catalogPlan, prev));
            showToast(`Plan updated to ${res.data?.planName || 'selected plan'}.`);
            try {
                const usageRes = await axiosInstance.get(`/billing/usage/${firmId}`);
                setUsage(usageRes.data);
            } catch {
                showToast('Plan saved, but usage rates failed to reload. Refresh the page.', 'error');
            }
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to select plan.', 'error');
        } finally {
            setSelectingPlanId(null);
        }
    };

    const handleSetupPayment = async () => {
        setSetupLoading(true);
        try {
            const res = await axiosInstance.post('/billing/setup-intent', { firmId: Number(firmId) });
            const { clientSecret, publishableKey } = res.data;

            if (!window.Stripe) {
                showToast('Stripe.js not loaded. Please refresh and try again.', 'error');
                return;
            }

            const stripe = window.Stripe(publishableKey);
            const elements = stripe.elements({ clientSecret });
            const paymentEl = elements.create('payment');

            const container = document.getElementById('stripe-element-container');
            if (container) {
                container.innerHTML = '';
                paymentEl.mount('#stripe-element-container');
                stripeRef.current = { stripe, elements };
            }
        } catch (err) {
            showToast(err?.response?.data?.message || 'Failed to initialize payment setup.', 'error');
        } finally {
            setSetupLoading(false);
        }
    };

    const handleConfirmPayment = async () => {
        if (!stripeRef.current) return;
        const { stripe, elements } = stripeRef.current;
        const { error, setupIntent } = await stripe.confirmSetup({
            elements,
            redirect: 'if_required',
        });
        if (error) {
            showToast(error.message, 'error');
            return;
        }
        await axiosInstance.post('/billing/confirm-payment-method', {
            firmId: Number(firmId),
            paymentMethodId: setupIntent.payment_method,
        });
        showToast('Payment method saved successfully.');
        document.getElementById('stripe-element-container').innerHTML = '';
        stripeRef.current = null;
        localStorage.removeItem('firmSuspended');
        localStorage.setItem('firmStatus', 'active');
        await reloadSubscription();
    };

    const handleRunCharges = async (dryRun) => {
        if (!firmId) return;
        const periodHint = 'previous calendar month (UTC)';
        const ok = window.confirm(
            dryRun
                ? `Dry-run monthly charges for this firm (${periodHint})? No Stripe charge will be made.`
                : `Charge this firm's card on file for ${periodHint}? (Stripe sandbox)`
        );
        if (!ok) return;
        setChargeRunning(true);
        try {
            const res = await axiosInstance.post('/billing/run-charges', {
                firmId: Number(firmId),
                dryRun: !!dryRun,
            });
            const row = Array.isArray(res.data?.results) ? res.data.results[0] : null;
            const outcome = row?.outcome || 'done';
            const amount = row?.totalAmount != null ? ` $${Number(row.totalAmount).toFixed(2)}` : '';
            const reason = row?.reason || row?.error || row?.detail || '';
            const isErr = outcome === 'failed' || (outcome === 'skipped' && row?.reason === 'not_eligible');
            showToast(`${outcome}${amount}${reason ? ` — ${reason}` : ''}`, isErr ? 'error' : 'success');
            if (!dryRun) await reloadHistory();
        } catch (err) {
            showToast(err?.response?.data?.message || 'Charge run failed.', 'error');
        } finally {
            setChargeRunning(false);
        }
    };

    if (!firmId) return <div style={{ padding: 40, color: '#94a3b8' }}>No firm associated with your account.</div>;

    if (loading) return <div style={{ textAlign: 'center', padding: 60 }}><Spinner /></div>;

    const isTrialing = sub?.isTrialing;
    const hasPM = sub?.hasPaymentMethod;
    const isSuspended = String(sub?.status || '').toLowerCase() === 'suspended';
    const currentPlan = plans.find(p => Number(p.id) === Number(sub?.planId));
    // Prefer catalog rates for the Current plan so unit prices always match the selected card
    const unitCompany = Number(currentPlan?.companyMonthlyPrice ?? usage?.pricing?.companyMonthly ?? 0);
    const unitEmployee = Number(currentPlan?.employeeMonthlyPrice ?? usage?.pricing?.employeeMonthly ?? 0);
    const unitContractor = Number(currentPlan?.contractorMonthlyPrice ?? usage?.pricing?.contractorMonthly ?? 0);

    return (
        <div style={{ maxWidth: 900 }}>
            {toast && (
                <div style={{
                    position: 'fixed', top: 20, right: 24, zIndex: 9999,
                    background: toast.type === 'error' ? '#fef2f2' : '#f0fdf4',
                    border: `1px solid ${toast.type === 'error' ? '#fca5a5' : '#86efac'}`,
                    color: toast.type === 'error' ? '#dc2626' : '#16a34a',
                    borderRadius: 10, padding: '12px 20px', boxShadow: '0 4px 16px rgba(0,0,0,0.1)', fontSize: 14, fontWeight: 500,
                }}>
                    {toast.type === 'error' ? '✗ ' : '✓ '}{toast.msg}
                </div>
            )}

            <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1e293b', marginBottom: 20 }}>Billing & Subscription</h2>

            {isSuspended && (
                <div style={{
                    background: '#fef2f2', border: '1px solid #fca5a5',
                    borderRadius: 10, padding: '14px 18px', marginBottom: 20,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
                    flexWrap: 'wrap',
                }}>
                    <div>
                        <div style={{ fontWeight: 700, color: '#991b1b', fontSize: 14 }}>Account suspended — payment failed</div>
                        <div style={{ fontSize: 13, color: '#b91c1c', marginTop: 2 }}>
                            Update your card below. After a successful charge (or SuperAdmin reactivation), status returns to active.
                        </div>
                    </div>
                    <button type="button" onClick={handleSetupPayment} disabled={setupLoading} style={{
                        background: '#dc2626', color: '#fff', border: 'none',
                        borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                    }}>
                        {setupLoading ? <Spinner /> : 'Update payment method'}
                    </button>
                </div>
            )}

            {/* Status banner */}
            {isTrialing && !isSuspended && (
                <div style={{
                    background: '#fffbeb', border: '1px solid #fcd34d',
                    borderRadius: 10, padding: '14px 18px', marginBottom: 20,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                    <div>
                        <div style={{ fontWeight: 700, color: '#92400e', fontSize: 14 }}>⏱ Free Trial Active</div>
                        <div style={{ fontSize: 13, color: '#b45309', marginTop: 2 }}>
                            {sub.trialDaysLeft} day{sub.trialDaysLeft !== 1 ? 's' : ''} remaining — trial ends {new Date(sub.trialEndsAt).toLocaleDateString()}
                        </div>
                    </div>
                    {!hasPM && (
                        <button onClick={handleSetupPayment} disabled={setupLoading} style={{
                            background: '#1B75F0', color: '#fff', border: 'none',
                            borderRadius: 8, padding: '8px 18px', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                        }}>
                            {setupLoading ? <Spinner /> : 'Add Payment Method'}
                        </button>
                    )}
                </div>
            )}

            {sub?.planId && !hasPM && (
                <div style={{
                    background: '#eff6ff', border: '1px solid #93c5fd',
                    borderRadius: 10, padding: '12px 16px', marginBottom: 20,
                    fontSize: 13, color: '#1e40af',
                }}>
                    Add a card before your trial ends so monthly usage charges can run automatically.
                </div>
            )}

            {/* Plan catalog (sell-flow) */}
            <Card title="Choose a Plan" style={{ marginBottom: 20 }}>
                {plans.length === 0 ? (
                    <div style={{ color: '#94a3b8', fontSize: 13 }}>No active plans available. Ask SuperAdmin to publish a package.</div>
                ) : (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
                        {plans.map((p) => {
                            const current = Number(sub?.planId) === Number(p.id);
                            const fmt = (n) => `$${Number(n ?? 0).toFixed(2)}`;
                            const lim = (n) => (n == null || Number(n) < 0 ? 'Unlimited' : String(n));
                            return (
                                <div
                                    key={p.id}
                                    style={{
                                        border: `2px solid ${current ? '#1B75F0' : '#e2e8f0'}`,
                                        borderRadius: 12,
                                        padding: '16px 14px',
                                        background: current ? '#f0f7ff' : '#fff',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: 8,
                                    }}
                                >
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                                        <div style={{ fontSize: 15, fontWeight: 800, color: '#1e293b' }}>{p.name}</div>
                                        {current && (
                                            <span style={{
                                                fontSize: 10, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                                                background: '#1B75F0', color: '#fff', whiteSpace: 'nowrap',
                                            }}>Current</span>
                                        )}
                                    </div>
                                    {p.description && (
                                        <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.4 }}>{p.description}</div>
                                    )}
                                    <div style={{ fontSize: 12, color: '#334155', lineHeight: 1.6 }}>
                                        <div>Company: <strong>{fmt(p.companyMonthlyPrice)}</strong>/mo</div>
                                        <div>Employee: <strong>{fmt(p.employeeMonthlyPrice)}</strong>/mo</div>
                                        <div>Contractor: <strong>{fmt(p.contractorMonthlyPrice)}</strong>/mo</div>
                                        <div style={{ marginTop: 4, color: '#64748b' }}>
                                            Limits: {lim(p.maxCompanies)} cos · {lim(p.maxEmployees)} emp · {lim(p.maxUsers)} users
                                        </div>
                                        {Number(p.trialDays) > 0 && (
                                            <div style={{ color: '#b45309' }}>{p.trialDays}-day trial on first select</div>
                                        )}
                                    </div>
                                    <button
                                        type="button"
                                        disabled={current || selectingPlanId != null}
                                        onClick={() => handleSelectPlan(p.id)}
                                        style={{
                                            marginTop: 'auto',
                                            background: current ? '#e2e8f0' : '#1B75F0',
                                            color: current ? '#64748b' : '#fff',
                                            border: 'none',
                                            borderRadius: 8,
                                            padding: '8px 12px',
                                            fontSize: 13,
                                            fontWeight: 600,
                                            cursor: current || selectingPlanId != null ? 'default' : 'pointer',
                                        }}
                                    >
                                        {selectingPlanId === p.id ? 'Selecting…' : current ? 'Selected' : 'Select plan'}
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Card>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                {/* Current usage */}
                <Card title="Current Usage">
                    <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
                        <StatBox label="Companies"   value={usage?.companyCount    ?? 0} sub={`$${unitCompany}/mo each`} />
                        <StatBox label="Employees"   value={usage?.employeeCount   ?? 0} sub={`$${unitEmployee}/mo each`} color="#3b82f6" />
                        <StatBox label="Contractors" value={usage?.contractorCount ?? 0} sub={`$${unitContractor}/mo each`} color="#8b5cf6" />
                    </div>
                    <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: 13, color: '#64748b' }}>Estimated monthly total</span>
                        <span style={{ fontSize: 20, fontWeight: 800, color: '#1B75F0' }}>
                            ${(usage?.estimatedMonthly ?? 0).toFixed(2)}
                        </span>
                    </div>
                </Card>

                {/* Subscription info */}
                <Card title="Subscription">
                    <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 12, color: '#64748b' }}>Plan</div>
                        <div style={{ fontSize: 15, fontWeight: 700, color: '#1e293b', marginTop: 2 }}>
                            {sub?.planName || 'No plan assigned'}
                        </div>
                    </div>
                    <div style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 12, color: '#64748b' }}>Status</div>
                        <div style={{ marginTop: 4 }}>
                            <span style={{
                                fontSize: 12, fontWeight: 700, padding: '3px 10px', borderRadius: 20,
                                background: isSuspended ? '#fef2f2' : isTrialing ? '#fefce8' : hasPM ? '#f0fdf4' : '#fef2f2',
                                color: isSuspended ? '#dc2626' : isTrialing ? '#ca8a04' : hasPM ? '#16a34a' : '#dc2626',
                                border: `1px solid ${isSuspended ? '#fca5a5' : isTrialing ? '#fde047' : hasPM ? '#86efac' : '#fca5a5'}`,
                            }}>
                                {isSuspended
                                    ? 'Suspended'
                                    : isTrialing
                                        ? `Trial (${sub.trialDaysLeft}d left)`
                                        : sub?.status || 'Unknown'}
                            </span>
                        </div>
                    </div>
                    <div>
                        <div style={{ fontSize: 12, color: '#64748b' }}>Payment Method</div>
                        <div style={{ fontSize: 13, color: '#374151', marginTop: 2 }}>
                            {hasPM ? '✓ Card on file' : '✗ No payment method'}
                        </div>
                        <button type="button" onClick={handleSetupPayment} disabled={setupLoading} style={{
                            marginTop: 10,
                            background: isSuspended ? '#dc2626' : '#1B75F0',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 7,
                            padding: '7px 16px',
                            fontSize: 12,
                            fontWeight: 600,
                            cursor: setupLoading ? 'default' : 'pointer',
                        }}>
                            {setupLoading
                                ? <Spinner />
                                : (hasPM ? 'Update Card' : 'Add Card')}
                        </button>
                    </div>
                </Card>
            </div>

            {/* Stripe payment element mount point */}
            <div id="stripe-element-container" style={{ marginBottom: 16 }} />
            {stripeRef.current && (
                <button onClick={handleConfirmPayment} style={{
                    background: '#1B75F0', color: '#fff', border: 'none',
                    borderRadius: 8, padding: '10px 24px', fontSize: 14, fontWeight: 600,
                    cursor: 'pointer', marginBottom: 20,
                }}>
                    Save Payment Method
                </button>
            )}

            {isSuperAdmin && (
                <Card title="SuperAdmin — Monthly Charges" style={{ marginBottom: 20 }}>
                    <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
                        Runs Stripe sandbox off-session charge for this firm for the previous calendar month (UTC).
                        Firm must have a plan, card on file, and trial ended.
                    </p>
                    <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                        <button
                            type="button"
                            disabled={chargeRunning}
                            onClick={() => handleRunCharges(true)}
                            style={{
                                background: '#fff', color: '#334155', border: '1px solid #cbd5e1',
                                borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600,
                                cursor: chargeRunning ? 'wait' : 'pointer',
                            }}
                        >
                            {chargeRunning ? 'Running…' : 'Dry run'}
                        </button>
                        <button
                            type="button"
                            disabled={chargeRunning}
                            onClick={() => handleRunCharges(false)}
                            style={{
                                background: '#0f766e', color: '#fff', border: 'none',
                                borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600,
                                cursor: chargeRunning ? 'wait' : 'pointer',
                            }}
                        >
                            {chargeRunning ? 'Running…' : 'Run monthly charges'}
                        </button>
                    </div>
                </Card>
            )}

            {/* Billing history */}
            <Card title="Billing History">
                {history.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '24px 0', color: '#94a3b8', fontSize: 13 }}>
                        No billing history yet. Invoices will appear here after your first billing cycle.
                    </div>
                ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                            <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                                {['Period', 'Companies', 'Employees', 'Contractors', 'Pro-rate', 'Total', 'Status'].map(h => (
                                    <th key={h} style={{ padding: '8px 10px', fontSize: 11, fontWeight: 700, color: '#64748b', textAlign: 'left', textTransform: 'uppercase' }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {history.map(cycle => (
                                <tr key={cycle.id} style={{ borderBottom: '1px solid #f8f8f8' }}>
                                    <td style={{ padding: '10px', fontSize: 13 }}>
                                        {new Date(cycle.periodStart).toLocaleDateString()} – {new Date(cycle.periodEnd).toLocaleDateString()}
                                    </td>
                                    <td style={{ padding: '10px', fontSize: 13 }}>{cycle.companyCount}</td>
                                    <td style={{ padding: '10px', fontSize: 13 }}>{cycle.employeeCount}</td>
                                    <td style={{ padding: '10px', fontSize: 13 }}>{cycle.contractorCount}</td>
                                    <td style={{ padding: '10px', fontSize: 13, color: Number(cycle.proRatedAmount) > 0 ? '#0f766e' : '#94a3b8' }}
                                        title="Company mid-month discount">
                                        {Number(cycle.proRatedAmount) > 0
                                            ? `−$${Number(cycle.proRatedAmount).toFixed(2)}`
                                            : '—'}
                                    </td>
                                    <td style={{ padding: '10px', fontSize: 13, fontWeight: 700, color: '#1B75F0' }}>${Number(cycle.totalAmount).toFixed(2)}</td>
                                    <td style={{ padding: '10px' }}>
                                        <span style={{
                                            fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 20,
                                            background: cycle.status === 'paid' ? '#f0fdf4' : cycle.status === 'failed' ? '#fef2f2' : '#fef9c3',
                                            color: cycle.status === 'paid' ? '#16a34a' : cycle.status === 'failed' ? '#dc2626' : '#92400e',
                                        }} title={cycle.errorMessage || ''}>{cycle.status}</span>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </Card>

            <div style={{ marginTop: 16, fontSize: 12, color: '#94a3b8', lineHeight: 1.6 }}>
                <strong>Billing note:</strong> Companies added mid-month are pro-rated from the day they are added.
                Employee & contractor charges for your first billing cycle are post-pay (billed at month end).
                Subsequent cycles are pre-pay (billed at the start of each month).
            </div>
        </div>
    );
};

export default BillingPage;
